import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";

// @mui material components
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom hooks
import { useAdvisers } from "../hooks/useAdvisers";
import { useAdviserDetails } from "../hooks/useAdviserDetails";
import { useDocumentGeneration } from "../hooks/useDocumentGeneration";

// Components
import { AdviserSelection } from "./AdviserSelection";
import { AdviserDetails } from "./AdviserDetails";
import { DocumentPreview } from "./DocumentPreview";
import { InitialStep } from "./InitialStep";
import { LoadingState } from "./LoadingState";
import { ErrorState } from "./ErrorState";

// Utilities
import { getErrorMessage, getLoadingMessage } from "../utils/errorUtils";
import { generateMixedOrientationPDF } from "../utils/reactPdfUtils";

// Step constants - use UPPER_CASE for constants
const INITIAL_STEP = "initial";
const HTML_STEP = "html";

/**
 * Document Modal Component
 *
 * Generates and displays a contract document with:
 * - Template selection and client data integration
 * - HTML preview with professional styling
 * - PDF-ready view for printing
 * - Variable replacement with client information
 * - Adviser selection dropdown
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} [props.templateId] - Template ID to generate document from
 * @param {string} [props.templateName] - Template name for display
 * @param {string} [props.clientId] - Client ID for data replacement
 * @param {string} [props.clientType] - Client type ('individual' or 'entity')
 * @param {string} [props.clientName] - Client name for display
 * @param {string} [props.adviserId] - Optional adviser ID
 */
function DocumentModal({
  open,
  onClose,
  templateId,
  templateName,
  clientId,
  clientType,
  clientName,
  adviserId = null,
}) {
  // State
  const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
  const [documentData, setDocumentData] = useState(null);
  const [processedHtmlContent, setProcessedHtmlContent] = useState("");
  const previewRef = useRef();
  const [loading, setLoading] = useState(false);

  // Custom hooks
  const {
    advisers,
    selectedAdviser,
    advisersLoading,
    advisersError,
    setSelectedAdviser,
    fetchAdvisers,
    resetAdvisers,
  } = useAdvisers();

  const {
    adviserDetails,
    adviserDetailsLoading,
    adviserDetailsError,
    fetchAdviserDetails,
    resetAdviserDetails,
  } = useAdviserDetails();

  const {
    loading: documentGenerationLoading,
    error: documentGenerationError,
    documentData: generatedDocumentData,
    retryCount,
    isRetrying,
    generateDocumentData,
    handleRetry,
    resetDocument,
  } = useDocumentGeneration();

  // Memoized values
  const modalTitle = useMemo(() => {
    switch (currentStep) {
      case HTML_STEP:
        return "Document Preview";
      default:
        return "Generate Document";
    }
  }, [currentStep]);

  const loadingMessage = useMemo(
    () => getLoadingMessage(isRetrying, retryCount),
    [isRetrying, retryCount]
  );

  // Effects
  useEffect(() => {
    if (open) {
      fetchAdvisers((adviserId) => {
        if (adviserId && clientId) {
          fetchAdviserDetails(adviserId, clientId);
        }
      });
    }
  }, [open, fetchAdvisers, fetchAdviserDetails, clientId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.isExporting) {
        setLoading(window.isExporting());
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Event handlers
  const handleClose = useCallback(() => {
    setCurrentStep(INITIAL_STEP);
    resetDocument();
    resetAdvisers();
    resetAdviserDetails();
    onClose();
  }, [resetDocument, resetAdvisers, resetAdviserDetails, onClose]);

  const handleGenerateDocument = useCallback(() => {
    if (!selectedAdviser) return;
    generateDocumentData(templateId, clientId, clientType, selectedAdviser);
  }, [selectedAdviser, templateId, clientId, clientType, generateDocumentData]);

  const handleRetryClick = useCallback(() => {
    handleRetry();
    generateDocumentData(
      templateId,
      clientId,
      clientType,
      selectedAdviser,
      true
    );
  }, [
    handleRetry,
    generateDocumentData,
    templateId,
    clientId,
    clientType,
    selectedAdviser,
  ]);

  const handleAdviserChange = useCallback(
    (adviserId) => {
      setSelectedAdviser(adviserId);
      if (adviserId) {
        fetchAdviserDetails(adviserId, clientId);
      } else {
        resetAdviserDetails();
      }
    },
    [setSelectedAdviser, fetchAdviserDetails, resetAdviserDetails, clientId]
  );

  const handleBackToInitial = useCallback(() => {
    setCurrentStep(INITIAL_STEP);
    resetDocument();
    resetAdviserDetails();
  }, [resetDocument, resetAdviserDetails]);

  /**
   * Process HTML: replace input fields with their values as text
   * @param {string} html - HTML content to process
   * @returns {string} Processed HTML content
   */
  const extractInputsAsText = useCallback((html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Replace all inputs
    tempDiv.querySelectorAll("input").forEach((input) => {
      const value = input.value || input.getAttribute("value") || "________";
      const textNode = document.createTextNode(value);
      input.parentNode.replaceChild(textNode, input);
    });

    // Replace all textareas
    tempDiv.querySelectorAll("textarea").forEach((textarea) => {
      const value =
        textarea.value ||
        textarea.textContent ||
        textarea.getAttribute("value") ||
        "________";
      const textNode = document.createTextNode(value);
      textarea.parentNode.replaceChild(textNode, textarea);
    });

    return tempDiv.innerHTML;
  }, []);

  /**
   * Handle PDF export with mixed orientation support
   * @returns {Promise<void>}
   */
  const handleShowPdf = useCallback(async () => {
    if (!processedHtmlContent) {
      console.error("No processed HTML content available");
      alert("No document content available. Please try again.");
      return;
    }

    const processed = extractInputsAsText(processedHtmlContent);

    try {
      await generateMixedOrientationPDF(processed, templateName, clientName);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  }, [processedHtmlContent, extractInputsAsText, templateName, clientName]);

  // Update step when document is generated
  useEffect(() => {
    if (generatedDocumentData && currentStep === INITIAL_STEP) {
      setDocumentData(generatedDocumentData);
      setCurrentStep(HTML_STEP);
    }
  }, [generatedDocumentData, currentStep]);

  console.log(window.handleLoading, "kl");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          height: "95vh",
          maxWidth: "95vw",
          maxHeight: "95vh",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
      aria-labelledby="document-modal-title"
    >
      <DialogTitle id="document-modal-title" sx={{ pb: 2 }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            {currentStep === HTML_STEP && (
              <IconButton
                onClick={handleBackToInitial}
                aria-label="Go back"
                sx={{ mr: 1 }}
              >
                <Icon>arrow_back</Icon>
              </IconButton>
            )}
            <MDBox>
              <MDTypography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                {modalTitle}
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {templateName} - {clientName}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" gap={1} alignItems="center">
            {/* Zoom Controls */}
            <MDBox
              display="flex"
              gap={1}
              alignItems="center"
              sx={{
                background: "rgba(255,255,255,0.9)",
                borderRadius: 2,
                padding: "4px 8px",
                border: "1px solid #e0e0e0",
              }}
            >
              <IconButton
                onClick={() => window.handleZoomOut?.()}
                size="small"
                sx={{ color: "#1a365d" }}
              >
                <Icon>zoom_out</Icon>
              </IconButton>
              <MDTypography
                variant="body2"
                sx={{
                  minWidth: "50px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#1a365d",
                }}
              >
                {window.currentZoomLevel
                  ? Math.round(window.currentZoomLevel * 100)
                  : 100}
                %
              </MDTypography>
              <IconButton
                onClick={() => window.handleZoomIn?.()}
                size="small"
                sx={{ color: "#1a365d" }}
              >
                <Icon>zoom_in</Icon>
              </IconButton>
              <IconButton
                onClick={() => window.handleZoomReset?.()}
                size="small"
                sx={{ color: "#1a365d" }}
              >
                <Icon>refresh</Icon>
              </IconButton>
            </MDBox>

            {/* Export Button - Only show on document preview step */}
            {currentStep === HTML_STEP && (
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => window.handleExportPdf?.()}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" sx={{ color: "white" }} />
                  ) : (
                    <Icon>picture_as_pdf</Icon>
                  )
                }
                size="small"
                disabled={loading}
                sx={{
                  borderRadius: "12px",
                  px: 2,
                  py: 0.5,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  boxShadow: "0 2px 8px rgba(52, 152, 219, 0.2)",
                  textTransform: "none",
                  letterSpacing: 0.5,
                }}
              >
                {loading ? "Exporting..." : "Export PDF"}
              </MDButton>
            )}

            <IconButton onClick={handleClose} aria-label="Close modal">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <DialogContent>
        {documentGenerationLoading && <LoadingState message={loadingMessage} />}

        {documentGenerationError && (
          <ErrorState
            error={documentGenerationError}
            onRetry={handleRetryClick}
          />
        )}

        {/* Initial Step - Generate Document */}
        {currentStep === INITIAL_STEP &&
          !documentGenerationLoading &&
          !documentGenerationError && (
            <InitialStep
              templateName={templateName}
              clientName={clientName}
              clientType={clientType}
              advisers={advisers}
              selectedAdviser={selectedAdviser}
              advisersLoading={advisersLoading}
              advisersError={advisersError}
              adviserDetails={adviserDetails}
              adviserDetailsLoading={adviserDetailsLoading}
              adviserDetailsError={adviserDetailsError}
              onAdviserChange={handleAdviserChange}
              onGenerateDocument={handleGenerateDocument}
            />
          )}

        {/* HTML Step - Document Preview */}
        {currentStep === HTML_STEP &&
          documentData &&
          !documentGenerationLoading && (
            <div ref={previewRef}>
              <DocumentPreview
                documentData={documentData}
                adviserDetails={adviserDetails}
                templateName={templateName}
                clientName={clientName}
                onShowPdf={handleShowPdf}
                onContentReady={setProcessedHtmlContent}
              />
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

DocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templateId: PropTypes.string,
  templateName: PropTypes.string,
  clientId: PropTypes.string,
  clientType: PropTypes.string,
  clientName: PropTypes.string,
  adviserId: PropTypes.string,
};

DocumentModal.defaultProps = {
  templateId: null,
  templateName: "",
  clientId: null,
  clientType: "individual",
  clientName: "",
  adviserId: null,
};

export default DocumentModal;
